import "./App.css";
import "./styles/Bootstrap.css";
import "./styles/Style.css";
import "./styles/Responsive.css";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CertificatePermissionGate from "./components/CertificatePermissionGate.jsx";
import { Helmet } from "react-helmet";

const Home = lazy(() => import("./pages/Home.jsx"));
const About = lazy(() => import("./pages/About.jsx"));
const Course = lazy(() => import("./pages/Course.jsx"));
const Gallery = lazy(() => import("./pages/Gallery.jsx"));
const Contact = lazy(() => import("./pages/Contact.jsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.jsx"));
const CTFCourses = lazy(() => import("./pages/Course_Details/CourseDetails1.jsx"));
const University = lazy(() => import("./pages/Course_Details/CourseDetails2.jsx"));
const Mauritius = lazy(() => import("./pages/Course_Details/CourseDetails3.jsx"));
const Internships = lazy(() => import("./pages/Course_Details/CourseDetails4.jsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.jsx"));
const AdminLogin = lazy(() => import("./pages/auth/AdminLogin.jsx"));
const MemberLogin = lazy(() => import("./pages/auth/MemberLogin.jsx"));
const StudentLogin = lazy(() => import("./pages/auth/StudentLogin.jsx"));
const MemberResetPassword = lazy(() => import("./pages/auth/MemberResetPassword.jsx"));
const StudentResetPassword = lazy(() => import("./pages/auth/StudentResetPassword.jsx"));
const MemberLayout = lazy(() => import("./member/MemberLayout.jsx"));
const MemberDashboard = lazy(() => import("./pages/member/Dashboard.jsx"));
const MemberWorksheets = lazy(() => import("./pages/member/Worksheets.jsx"));
const MemberStudents = lazy(() => import("./pages/member/Students.jsx"));
const MemberStudentForm = lazy(() => import("./pages/member/StudentForm.jsx"));
const MemberCourses = lazy(() => import("./pages/member/Courses.jsx"));
const MemberAddCourse = lazy(() => import("./pages/member/AddCourse.jsx"));
const MemberCourseMaterials = lazy(() => import("./pages/member/CourseMaterials.jsx"));
const MemberCourseMaterialForm = lazy(() => import("./pages/member/CourseMaterialForm.jsx"));
const MemberFees = lazy(() => import("./pages/member/Fees.jsx"));
const MemberFeeForm = lazy(() => import("./pages/member/FeeForm.jsx"));
const MemberAssignmentSubmissions = lazy(() => import("./pages/member/AssignmentSubmissions.jsx"));
const MemberAttendance = lazy(() => import("./pages/member/Attendance.jsx"));
const MemberAttendanceForm = lazy(() => import("./pages/member/AttendanceForm.jsx"));
const MemberAttendanceCalendar = lazy(() => import("./pages/member/MemberAttendanceCalendar.jsx"));
const MemberSettings = lazy(() => import("./pages/member/Settings.jsx"));
const MemberAnnouncements = lazy(() => import("./pages/member/Announcements.jsx"));
const MemberAnnouncementForm = lazy(() => import("./pages/member/AnnouncementForm.jsx"));
const StudentLayout = lazy(() => import("./student/StudentLayout.jsx"));
const StudentDashboard = lazy(() => import("./pages/student/StudentDashboard.jsx"));
const StudentCourseMaterials = lazy(() => import("./pages/student/StudentCourseMaterials.jsx"));
const StudentAssignments = lazy(() => import("./pages/student/StudentAssignments.jsx"));
const StudentFees = lazy(() => import("./pages/student/StudentFees.jsx"));
const StudentAttendance = lazy(() => import("./pages/student/StudentAttendance.jsx"));
const StudentIdCard = lazy(() => import("./pages/student/StudentIdCard.jsx"));
const StudentSettings = lazy(() => import("./pages/student/Settings.jsx"));
const AdminLayout = lazy(() => import("./admin/Layout.jsx"));
const MemberChatInquiries = lazy(() => import("./pages/member/ChatInquiries.jsx"));
const AddCourse = lazy(() => import("./pages/admin/AddCourse.jsx"));
const Courses = lazy(() => import("./pages/admin/Courses.jsx"));
const Fees = lazy(() => import("./pages/admin/Fees.jsx"));
const FeeForm = lazy(() => import("./pages/admin/FeeForm.jsx"));
const Franchises = lazy(() => import("./pages/admin/Franchises.jsx"));
const FranchiseForm = lazy(() => import("./pages/admin/FranchiseForm.jsx"));
const FranchisePercentage = lazy(() => import("./pages/admin/FranchisePercentage.jsx"));
const CourseMaterials = lazy(() => import("./pages/admin/CourseMaterials.jsx"));
const CourseMaterialForm = lazy(() => import("./pages/admin/CourseMaterialForm.jsx"));
const AssignmentSubmissions = lazy(() => import("./pages/admin/AssignmentSubmissions.jsx"));
const Attendance = lazy(() => import("./pages/admin/Attendance.jsx"));
const AttendanceForm = lazy(() => import("./pages/admin/AttendanceForm.jsx"));
const Settings = lazy(() => import("./pages/admin/Settings.jsx"));
const Members = lazy(() => import("./pages/admin/Members.jsx"));
const MemberForm = lazy(() => import("./pages/admin/MemberForm.jsx"));
const Students = lazy(() => import("./pages/admin/Students.jsx"));
const StudentForm = lazy(() => import("./pages/admin/StudentForm.jsx"));
const Worksheets = lazy(() => import("./pages/admin/Worksheets.jsx"));
const AdminLogout = lazy(() => import("./pages/admin/AdminLogout.jsx"));
const Certificate = lazy(() => import("./pages/admin/Certificate.jsx"));
const ChatInquiries = lazy(() => import("./pages/admin/ChatInquiries.jsx"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const StudentCertificateLookup = lazy(() => import("./pages/StudentCertificateLookup"));
const StudentAnnouncements = lazy(() => import("./pages/StudentAnnouncements"));
const AdminAnnouncements = lazy(() => import("./pages/admin/Announcements.jsx"));
const AdminAnnouncementForm = lazy(() => import("./pages/admin/AnnouncementForm.jsx"));
const ChatWidget = lazy(() => import("./components/ChatWidget.jsx"));
const ScrollTopButton = lazy(() => import("./components/ScrollTopButton.jsx"));
const MobileContactBar = lazy(() => import("./components/MobileContactBar.jsx"));

function PublicLayout() {
  const [showDeferredWidgets, setShowDeferredWidgets] = useState(false);
  const [showChatWidget, setShowChatWidget] = useState(false);

  useEffect(() => {
    document.body.classList.add("has-fixed-navbar");
    return () => document.body.classList.remove("has-fixed-navbar");
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const showWidgets = () => setShowDeferredWidgets(true);
    const id = window.setTimeout(() => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(showWidgets, { timeout: 1500 });
      } else {
        showWidgets();
      }
    }, 1200);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let idleId;
    let chatShown = false;
    const isMobile = window.matchMedia("(max-width: 991px)").matches;

    const revealChat = () => {
      if (chatShown) return;
      chatShown = true;
      setShowChatWidget(true);
      window.removeEventListener("pointerdown", onInteraction);
      window.removeEventListener("keydown", onInteraction);
      window.removeEventListener("touchstart", onInteraction);
    };

    const onInteraction = () => {
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(revealChat, { timeout: 1200 });
      } else {
        revealChat();
      }
    };

    const fallbackTimer = window.setTimeout(() => {
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(revealChat, { timeout: 2500 });
      } else {
        revealChat();
      }
    }, isMobile ? 12000 : 3200);

    window.addEventListener("pointerdown", onInteraction, { once: true, passive: true });
    window.addEventListener("keydown", onInteraction, { once: true });
    window.addEventListener("touchstart", onInteraction, { once: true, passive: true });

    return () => {
      window.clearTimeout(fallbackTimer);
      if (idleId && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
      window.removeEventListener("pointerdown", onInteraction);
      window.removeEventListener("keydown", onInteraction);
      window.removeEventListener("touchstart", onInteraction);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>CTF | Computer Technology Foundation</title>
        <meta
          name="description"
          content="CTF - Computer Technology Foundation offers professional computer training, certification courses and practical IT skills development."
        />
        <meta property="og:title" content="CTF | Computer Technology Foundation" />
        <meta
          property="og:description"
          content="Join CTF for industry-focused computer education, career-oriented IT courses and practical training."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${window.location.origin}/ctf-logo.png`} />
      </Helmet>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Navbar />
      <main id="main-content" tabIndex="-1">
        <Outlet />
      </main>
      <Footer />
      {showDeferredWidgets && (
        <>
          <Suspense fallback={null}>
            <ScrollTopButton />
          </Suspense>
          <Suspense fallback={null}>
            <MobileContactBar />
          </Suspense>
        </>
      )}
      {showChatWidget && (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}
    </>
  );
}

function WOWInit() {
  const location = useLocation();
  const wowRef = useRef(null);
  const wowFallbackObserverRef = useRef(null);
  const wowMutationObserverRef = useRef(null);
  const isPublicRoute = !/^\/(admin|member|student)(\/|$)/.test(location.pathname);

  useEffect(() => {
    if (!isPublicRoute) {
      return;
    }

    const initWOW = () => {
      if (typeof window !== "undefined" && window.WOW && !wowRef.current) {
        wowRef.current = new window.WOW({
          boxClass: "wow",
          animateClass: "animated",
          offset: 100,
          mobile: true,
          live: false,
        });
        wowRef.current.init();
        window.__ctfWow = wowRef.current;
      }
    };
    if (typeof window === "undefined") return;
    if (window.WOW) {
      initWOW();
      return;
    }

    const loadScript = () => {
      const existing = document.querySelector("script[data-wow-loader='1']");
      if (existing) {
        existing.addEventListener("load", initWOW, { once: true });
        return () => existing.removeEventListener("load", initWOW);
      }
      const script = document.createElement("script");
      script.src = `${process.env.PUBLIC_URL || ""}/wow.min.js`;
      script.defer = true;
      script.async = true;
      script.dataset.wowLoader = "1";
      script.addEventListener("load", initWOW, { once: true });
      document.body.appendChild(script);
      return () => script.removeEventListener("load", initWOW);
    };

    let cleanup = null;
    const start = () => {
      cleanup = loadScript();
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(start, { timeout: 1800 });
    } else {
      window.setTimeout(start, 300);
    }
    return () => {
      if (cleanup) cleanup();
    };
  }, [isPublicRoute]);

  // Fallback: ensure wow-style animations still trigger on SPA route changes.
  useEffect(() => {
    if (!isPublicRoute) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (!("IntersectionObserver" in window)) return;
    let rafId = null;
    let scrollTimeoutId = null;
    let rescanTimeoutId = null;
    let lateRescanTimeoutId = null;

    const playWow = (el) => {
      if (el.dataset.wowPlayed === "1") return;
      const wowDelay = el.getAttribute("data-wow-delay");
      const wowDuration = el.getAttribute("data-wow-duration");
      if (wowDelay) el.style.animationDelay = wowDelay;
      if (wowDuration) el.style.animationDuration = wowDuration;
      const hadAnimated = el.classList.contains("animated");
      el.classList.remove("animated");
      // Avoid unnecessary layout thrash on first play.
      if (hadAnimated) void el.offsetWidth;
      el.classList.add("animated");
      el.style.visibility = "visible";
      el.dataset.wowPlayed = "1";
    };

    if (!wowFallbackObserverRef.current) {
      wowFallbackObserverRef.current = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            playWow(el);
            observer.unobserve(el);
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
      );
    }

    const observer = wowFallbackObserverRef.current;
    const scanWowElements = (root = document) => {
      const els = root.querySelectorAll?.(".wow");
      if (!els?.length) return;
      const vh = window.innerHeight || document.documentElement.clientHeight || 800;

      els.forEach((el) => {
        if (el.dataset.wowObserved === "1") return;
        el.dataset.wowObserved = "1";
        delete el.dataset.wowPlayed;
        el.classList.remove("animated");

        // Immediate trigger for elements already in viewport after route navigation.
        const rect = el.getBoundingClientRect();
        const inView = rect.top < vh * 0.96 && rect.bottom > 0;
        if (inView) {
          playWow(el);
        } else {
          observer.observe(el);
        }
      });
    };

    const checkWowInView = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight || 800;
      const els = document.querySelectorAll(".wow");
      els.forEach((el) => {
        if (el.dataset.wowPlayed === "1") return;
        const rect = el.getBoundingClientRect();
        if (rect.top < vh * 0.96 && rect.bottom > 0) {
          playWow(el);
          observer.unobserve(el);
        }
      });
    };

    const scheduleCheck = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        checkWowInView();
      });
    };

    // Route change par fresh scan.
    document.querySelectorAll(".wow[data-wow-observed='1']").forEach((el) => {
      delete el.dataset.wowObserved;
      delete el.dataset.wowPlayed;
    });
    scanWowElements(document);
    scheduleCheck();

    // Lazy/dynamic content ke liye auto-scan.
    if (!wowMutationObserverRef.current && "MutationObserver" in window) {
      wowMutationObserverRef.current = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            if (node.classList?.contains("wow")) {
              delete node.dataset.wowObserved;
              delete node.dataset.wowPlayed;
              scanWowElements(node.parentElement || document);
              scheduleCheck();
              return;
            }
            if (node.querySelector?.(".wow")) {
              scanWowElements(node);
              scheduleCheck();
            }
          });
        });
      });
    }
    wowMutationObserverRef.current?.observe(document.body, { childList: true, subtree: true });

    const onScrollOrResize = () => {
      if (scrollTimeoutId) window.clearTimeout(scrollTimeoutId);
      scrollTimeoutId = window.setTimeout(scheduleCheck, 50);
    };

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("orientationchange", onScrollOrResize);

    // Late rescans for Suspense/lazy chunks that mount after route paint.
    rescanTimeoutId = window.setTimeout(() => {
      scanWowElements(document);
      scheduleCheck();
    }, 250);
    lateRescanTimeoutId = window.setTimeout(() => {
      scanWowElements(document);
      scheduleCheck();
    }, 900);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (scrollTimeoutId) window.clearTimeout(scrollTimeoutId);
      if (rescanTimeoutId) window.clearTimeout(rescanTimeoutId);
      if (lateRescanTimeoutId) window.clearTimeout(lateRescanTimeoutId);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("orientationchange", onScrollOrResize);
      wowMutationObserverRef.current?.disconnect();
    };
  }, [location.pathname, isPublicRoute]);

  useEffect(() => {
    if (!isPublicRoute) return;
    if (typeof window === "undefined") return;
    if (!wowRef.current || typeof wowRef.current.sync !== "function") return;
    let idleId;
    const rafId = window.requestAnimationFrame(() => {
      const syncWOW = () => wowRef.current?.sync?.();
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(syncWOW, { timeout: 1200 });
      } else {
        syncWOW();
      }
    });
    return () => {
      window.cancelAnimationFrame(rafId);
      if (idleId && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
    };
  }, [location.pathname, isPublicRoute]);

  useEffect(() => {
    return () => {
      if (wowFallbackObserverRef.current) {
        wowFallbackObserverRef.current.disconnect();
      }
      if (wowMutationObserverRef.current) {
        wowMutationObserverRef.current.disconnect();
      }
    };
  }, []);

  return null;
}

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <WOWInit />
        <ScrollToTopOnRouteChange />
        <Suspense fallback={null}>
        <Routes>
          {/* Public website (with navbar) */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="all-courses" element={<Course />} />
            <Route path="ctf_courses" element={<CTFCourses />} />
            <Route path="university" element={<University />} />
            <Route path="mauritius" element={<Mauritius />} />
            <Route path="internships" element={<Internships />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="contact" element={<Contact />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/verify-certificate" element={<VerifyCertificate />} />
            <Route path="/student-certificate" element={<StudentCertificateLookup />} />
            <Route path="/student-announcements" element={<StudentAnnouncements />} />
            <Route
              path="/student-exam-schedule"
              element={<Navigate to="/student-announcements" replace />}
            />
          </Route>

          {/* Admin / Member systems (Tailwind SPA, own layout) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/member/login" element={<MemberLogin />} />
          <Route path="/student/login" element={<StudentLogin />} />
          <Route path="/member/forgot-password" element={<Navigate to="/member/reset-password" replace />} />
          <Route path="/member/reset-password" element={<MemberResetPassword />} />
          <Route path="/student/forgot-password" element={<Navigate to="/student/reset-password" replace />} />
          <Route path="/student/reset-password" element={<StudentResetPassword />} />
          <Route path="/admin/logout" element={<AdminLogout />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="students" element={<Students studentScope="active" />} />
            <Route path="completed-students" element={<Students studentScope="completed" />} />
            <Route path="student-form" element={<StudentForm />} />
            <Route path="members" element={<Members />} />
            <Route path="member-form" element={<MemberForm />} />
            <Route path="courses" element={<Courses />} />
            <Route path="add-course" element={<AddCourse />} />
            <Route path="course-materials" element={<CourseMaterials />} />
            <Route path="course-material-form" element={<CourseMaterialForm />} />
            <Route path="assignment-submissions" element={<AssignmentSubmissions />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="attendance-form" element={<AttendanceForm />} />
            <Route path="worksheets" element={<Worksheets />} />
            <Route path="fees" element={<Fees />} />
            <Route path="fee-form" element={<FeeForm />} />
            <Route path="franchises" element={<Franchises />} />
            <Route path="franchise-form" element={<FranchiseForm />} />
            <Route path="franchise-percentage" element={<FranchisePercentage />} />
            <Route path="certificate" element={<Certificate />} />
            <Route path="chat-inquiries" element={<ChatInquiries />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="announcement-form" element={<AdminAnnouncementForm />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route
            path="/member"
            element={
              <ProtectedRoute requiredRole="member">
                <MemberLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/member/dashboard" replace />} />
            <Route path="dashboard" element={<MemberDashboard />} />
            <Route path="announcements" element={<MemberAnnouncements />} />
            <Route path="announcement-form" element={<MemberAnnouncementForm />} />
            <Route path="students" element={<MemberStudents studentScope="active" />} />
            <Route path="completed-students" element={<MemberStudents studentScope="completed" />} />
            <Route path="student-form" element={<MemberStudentForm />} />
            <Route path="courses" element={<MemberCourses />} />
            <Route path="add-course" element={<MemberAddCourse />} />
            <Route path="course-materials" element={<MemberCourseMaterials />} />
            <Route path="course-material-form" element={<MemberCourseMaterialForm />} />
            <Route path="assignment-submissions" element={<MemberAssignmentSubmissions />} />
            <Route path="worksheets" element={<MemberWorksheets />} />
            <Route path="attendance" element={<MemberAttendance />} />
            <Route path="attendance-form" element={<MemberAttendanceForm />} />
            <Route path="attendance-calendar" element={<MemberAttendanceCalendar />} />
            <Route path="fees" element={<MemberFees />} />
            <Route path="fee-form" element={<MemberFeeForm />} />
            <Route path="chat-inquiries" element={<MemberChatInquiries />} />
            <Route
              path="certificate"
              element={
                <CertificatePermissionGate>
                  <Certificate />
                </CertificatePermissionGate>
              }
            />
            <Route path="settings" element={<MemberSettings />} />
          </Route>

          <Route
            path="/student"
            element={
              <ProtectedRoute requiredRole="student">
                <StudentLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/student/dashboard" replace />} />
            <Route path="dashboard" element={<StudentDashboard />} />
            <Route path="course-materials" element={<StudentCourseMaterials />} />
            <Route path="assignments" element={<StudentAssignments />} />
            <Route path="fees" element={<StudentFees />} />
            <Route path="attendance" element={<StudentAttendance />} />
            <Route path="id-card" element={<StudentIdCard />} />
            <Route path="settings" element={<StudentSettings />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
